import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { sendAuthenticatedRequest } from "../common/functions";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import CustomerDetailsForm from "../common/components/CustomerDetailsForm";
import {
  CustomerFieldsConfig,
  CustomerProfile,
} from "../common/models/customerFields";
import { BuyerPreferences } from "./BuyerPreferences";

/**
 * What Kono holds about this customer, for them to correct.
 *
 * The fields are the ones configured internally under Tools > Customer Data
 * Configuration, so this page and the sign-up step never disagree about what is
 * asked for or what is compulsory.
 */
export default function MyInformation() {
  // Guests can browse the buyer app, so this page mounts before the sign-in has
  // been restored from storage. Fetching on mount sent a request with no token
  // and reported the 403 as "we could not load your details".
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [config, setConfig] = useState<CustomerFieldsConfig | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const resp = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_USER_API_BASE_URL}/profile`,
        { method: "GET" },
      );
      if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
      const data = await resp.json();
      setConfig(data.config);
      setProfile(data.profile);
    } catch (e) {
      console.error(e);
      setLoadError("We could not load your details. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (authIsLoading || !authUser) return;
    load();
  }, [authIsLoading, authUser, load]);

  return (
    <div className="mx-auto max-w-xl px-4 py-8">
      <h1 className="text-xl font-semibold">My information</h1>
      <p className="mt-2 mb-6 text-sm text-gray-600">
        This is what we have on file for you. Your company address is what we
        quote shipping from, so keeping it current keeps your delivery prices
        accurate.
      </p>

      {!authIsLoading && !authUser && (
        <p className="text-sm text-gray-700">
          Please{" "}
          <Link className="underline" to="/signin?redirect=/my-information">
            sign in
          </Link>{" "}
          to see your details.
        </p>
      )}
      {loadError && <div className="text-sm text-[#b91c1c]">{loadError}</div>}
      {(authIsLoading || (authUser && !config)) && !loadError && (
        <p className="text-sm text-gray-600">Loading…</p>
      )}

      {config && profile && (
        <>
          {saved && (
            <div className="mb-4 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
              Your details have been saved.
            </div>
          )}
          <CustomerDetailsForm
            config={config}
            initial={profile}
            submitLabel="Save changes"
            onSubmit={async (next: CustomerProfile) => {
              setSaved(false);
              const resp = await sendAuthenticatedRequest(
                `${process.env.REACT_APP_USER_API_BASE_URL}/profile`,
                { method: "POST", jsonBody: { profile: next } },
              );
              if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(
                  data.error || "We could not save your details. Please retry.",
                );
              }
              setSaved(true);
              await load();
              return true;
            }}
          />
        </>
      )}
      {authUser && <BuyerPreferences uid={authUser.uid} />}
    </div>
  );
}
